import { escapeHtml } from '../ui.js';
import {
  actionsFor, refFor, stateLine, waitingParty, waitAge, lastChased, reviewSince, checkAge,
  dueText, chasedText, daysText, dayKey,
} from '../tasks-v2.js';
import { wantsDiary } from './task-diary.js';
import { isSiteFlagTask, taskSourceLabel } from './site-checks-surfaces.js';
import { pickerHtml } from './task-reassign.js';
import { blockerFormHtml } from './task-blocker.js';

// THE TASK PANEL (design_handoff_tasks_v2, desktop: d-panel-*).
//
// A PANEL, NOT A MODAL: a 460px sheet on the right, and the page behind it does
// NOT dim — you opened a task to see it in context. The buttons are the ones
// actionsFor() gives for where the task stands, so the panel and the phone
// sheet offer the same moves. Pickers (Reassign, Send for check), the Waiting
// on… form and Return with comments open IN the panel, in place of the
// buttons, rather than stacking a dialog on top.
//
// No timer and no time log: time goes on the timesheet (the line under the
// buttons says so and links there).

const e = escapeHtml;
const pad2 = (n) => String(n).padStart(2, '0');
const dm = (iso) => (iso ? `${pad2(Number(iso.slice(8, 10)))}/${pad2(Number(iso.slice(5, 7)))}` : '');
const ago = (iso, today) => {
  const n = checkAge({ reviewSince: iso }, today);
  return n <= 0 ? 'today' : `${n} day${n === 1 ? '' : 's'} ago`;
};

function prop(label, value) {
  return `<div class="tk2-prop"><span class="tk2-plabel">${e(label)}</span><span class="tk2-pvalue">${value}</span></div>`;
}

/** The trail's time stamp, in local time: "18/10 09:20". */
const stamp = (at) => {
  const d = new Date(at);
  if (!at || Number.isNaN(d.getTime())) return String(at || '');
  return `${dm(dayKey(d))} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * @param opts.mode   'actions' (default) | 'assign' | 'check' | 'block' | 'return'
 * @param opts.acted  label of a Chase/Nudge just pressed ('Chased' | 'Nudged')
 */
export function panelHtml({
  task, people = [], project = null, tasks = [], meId = null, today = dayKey(), activity = null,
  mode = 'actions', draft = {}, error = '', acted = '', busy = false,
} = {}) {
  const t = task;
  const person = people.find((p) => p.userId === t.assigneeId) || null;
  const checker = people.find((p) => p.userId === t.checkerId) || null;
  const ref = refFor(t, project);
  const s = stateLine(t, people, today);
  const none = (txt) => `<span class="tk2-none">${e(txt)}</span>`;

  // A waiting task leads with what it waits on, in a box (d-panel-waiting).
  let waitBox = '';
  if (t.state === 'blocked') {
    const p = waitingParty(t, people);
    const n = waitAge(t, today);
    const chased = lastChased(t);
    const since = t.blocker && t.blocker.since;
    waitBox = `<div class="tk2-waitbox">
      <span class="tk2-waitbox-eb">Waiting on ${e(p.who)} · ${e(daysText(n).toLowerCase())}</span>
      ${p.what ? `<span class="tk2-waitbox-what">${e(p.what)}</span>` : ''}
      <span class="tk2-waitbox-meta">${since ? `Since ${e(dm(since))} · ` : ''}${chased ? `Last chased ${e(dm(chased))}` : 'Not chased yet'}</span>
      ${t.blocker && t.blocker.note ? `<span class="tk2-waitbox-note">${e(t.blocker.note)}</span>` : ''}
    </div>`;
  }

  const source = taskSourceLabel(t);
  const raised = source === 'Typed' ? none('Added by hand') : e(source);
  const siteLink = isSiteFlagTask(t) && project && project.number
    ? ` <a class="tk2-link" href="/projects/${encodeURIComponent(String(project.number))}#pdx-sc">Open site constraints</a>` : '';

  let body;
  if (mode === 'assign' || mode === 'check') {
    body = pickerHtml({ mode, task: t, tasks, people });
  } else if (mode === 'block') {
    body = blockerFormHtml({ draft, people, today, error });
  } else if (mode === 'return') {
    body = `<div class="tk2-return" role="group" aria-label="Return with comments">
      <div class="tk2-step-head"><span class="tk2-eb">Return with comments</span>
        <button type="button" class="tk2-link" data-step-cancel>Cancel</button></div>
      <label class="tk2-field"><span>What needs changing${person ? ` — ${e(person.name.split(' ')[0])} reads this` : ''}</span>
        <textarea data-return rows="4" maxlength="2000" placeholder="Say what to change before it comes back.">${e(draft.text || '')}</textarea></label>
      ${error ? `<p class="tk2-err" role="alert">${e(error)}</p>` : ''}
      <div class="tk2-btns"><button type="button" class="tk2-btn tk2-btn-p" data-return-save${String(draft.text || '').trim() ? '' : ' disabled'}>Return</button></div>
    </div>`;
  } else {
    const acts = actionsFor(t, meId).map((a) => {
      const who = a.key === 'chase' ? waitingParty(t, people).who : '';
      const pastToday = (a.key === 'chase' || a.key === 'nudge') && lastChased(t) === today;
      if (a.done && (acted === a.done || pastToday)) {
        return `<span class="tk2-btn tk2-btn-past" aria-live="polite">${e(a.done)}</span>`;
      }
      const label = a.key === 'chase' && who ? `Chase ${who}` : a.label;
      return `<button type="button" class="tk2-btn${a.primary ? ' tk2-btn-p' : ''}" data-action="${e(a.key)}"${busy ? ' disabled' : ''}>${e(label)}</button>`;
    }).join('');
    const more = [
      '<button type="button" class="tk2-link" data-edit>Edit</button>',
      wantsDiary(t) ? `<button type="button" class="tk2-link" data-diary>${t.diaryAt ? 'Move in the diary' : 'Put it in the diary'}</button>` : '',
    ].filter(Boolean).join('<span class="tk2-dotsep" aria-hidden="true">·</span>');
    body = `<div class="tk2-btns">${acts}</div>
      ${error ? `<p class="tk2-err" role="alert">${e(error)}</p>` : ''}
      <p class="tk2-more">${more}</p>`;
  }

  return `<div class="tk2-panel-in">
    <div class="tk2-pbar">
      <span class="tk2-pref">${ref ? e([ref.ref, ref.client].filter(Boolean).join(' · ')) : 'No project'}</span>
      <button type="button" class="tk2-pclose" data-close aria-label="Close">&times;</button>
    </div>
    <h2 class="tk2-ptitle">${e(t.title)}</h2>
    <p class="tk2-pstate tk2-tone-${e(s.tone)}"><span class="tk2-dot" aria-hidden="true"></span>${e(s.text)}</p>
    ${waitBox}
    <div class="tk2-props">
      ${prop('Assignee', person ? e(person.name) : none('Unassigned'))}
      ${prop('Checker', checker ? e(checker.name) : none('None'))}
      ${t.state === 'in_review' && reviewSince(t) ? prop('Sent for check', e(`${dm(reviewSince(t))} · ${ago(reviewSince(t), today)}`)) : ''}
      ${t.state === 'in_review' && t.nudgedAt ? prop('Last nudged', e(chasedText(t.nudgedAt, today))) : ''}
      ${prop('Due', t.dueDate ? `<span class="${t.state !== 'done' && t.dueDate < today ? 'tk2-hot' : ''}">${e(dueText(t, today))}</span>` : none('No date'))}
      ${prop('Raised from', raised + siteLink)}
    </div>
    ${t.description ? `<p class="tk2-pdesc">${e(t.description)}</p>` : ''}
    ${body}
    <p class="tk2-ptime">Time goes on the timesheet. <button type="button" class="tk2-link" data-logtime>${
      ref ? e(`Log time against ${ref.ref}`) : 'Log time'}</button></p>
    <div class="tk2-activity">
      <span class="tk2-eb tk2-eb-grey">Activity</span>
      ${activity == null ? '<p class="tk2-empty">Loading…</p>'
        : (activity.map((a) => `<div class="tk2-act-row">
            <span class="tk2-act-t"><b>${e(a.byName || (a.kind === 'system' ? 'Track' : a.by) || 'Track')}</b> ${e(a.text || '')}</span>
            <span class="tk2-act-w">${e(stamp(a.at))}</span>
          </div>`).join('') || '<p class="tk2-empty">Nothing yet.</p>')}
      <div class="tk2-comment">
        <input class="tk2-cinput" data-comment type="text" maxlength="2000" placeholder="Add a comment, Enter to post" aria-label="Add a comment">
      </div>
    </div>
  </div>`;
}

/**
 * Open (or replace) the panel.
 * @param opts.host      the element the panel lives in (the page's own mount,
 *                       so it dies with the route)
 * @param opts.onAction  async (key, payload) — the page writes; the panel only asks
 * @returns {{ close, refresh(task, ctx), taskId }}
 */
export function openTaskPanel(opts) {
  const { host, loadActivity, onComment, onAction, onEdit, onDiary, onLogTime, onClose } = opts;
  host.querySelector('.tk2-panel')?._close?.();
  const root = document.createElement('aside');
  root.className = 'tk2-panel';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'false');
  const st = {
    task: opts.task, people: opts.people || [], project: opts.project || null, tasks: opts.tasks || [],
    meId: opts.meId || null, activity: null, mode: opts.mode || 'actions', draft: {}, error: '', acted: '', busy: false,
  };
  const draw = () => {
    root.setAttribute('aria-label', st.task.title || 'Task');
    root.innerHTML = panelHtml({ ...st, today: dayKey() });
  };
  draw();
  host.appendChild(root);
  root.querySelector('.tk2-pclose')?.focus();

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    root.remove();
    document.removeEventListener('keydown', onKey);
    if (onClose) onClose();
  };
  root._close = close;
  const onKey = (ev) => {
    if (ev.key !== 'Escape') return;
    ev.preventDefault();
    if (st.mode !== 'actions') { st.mode = 'actions'; st.error = ''; return draw(); }
    close();
  };
  document.addEventListener('keydown', onKey);

  const reloadTrail = () => loadActivity().then((rows) => {
    if (closed) return;
    st.activity = rows || [];
    keepFocus(draw);
  }).catch(() => { if (!closed) { st.activity = []; draw(); } });
  reloadTrail();

  /** Redraw without losing what the person is typing. */
  function keepFocus(fn) {
    const a = document.activeElement;
    const sel = a && root.contains(a) ? (a.matches('[data-comment]') ? '[data-comment]'
      : a.matches('[data-return]') ? '[data-return]' : a.dataset && a.dataset.bf ? `[data-bf="${a.dataset.bf}"]` : null) : null;
    const val = sel ? a.value : null;
    fn();
    if (sel) {
      const n = root.querySelector(sel);
      if (n) { if (val != null) n.value = val; n.focus(); }
    }
  }

  async function act(key, payload) {
    st.busy = true; st.error = '';
    if (key === 'chase') st.acted = 'Chased';
    if (key === 'nudge') st.acted = 'Nudged';
    draw();
    try {
      await onAction(key, payload || {});
      st.mode = 'actions'; st.draft = {};
    } catch (err) {
      // A form that is not finished says what is missing; a refused write says
      // it did not save. Neither looks like success.
      st.error = err && err.plain ? err.message : `That did not save — ${(err && err.message) || err}`;
      st.acted = '';
    }
    st.busy = false;
    if (!closed) { draw(); reloadTrail(); }
  }

  root.addEventListener('click', (ev) => {
    if (ev.target.closest('[data-close]')) return close();
    if (ev.target.closest('[data-step-cancel]')) { st.mode = 'actions'; st.error = ''; st.draft = {}; return draw(); }
    if (ev.target.closest('[data-logtime]')) return onLogTime && onLogTime(st.task);
    if (ev.target.closest('[data-edit]')) return onEdit && onEdit(st.task);
    if (ev.target.closest('[data-diary]')) return onDiary && onDiary(st.task);
    const a = ev.target.closest('[data-action]');
    if (a && !st.busy) {
      const key = a.dataset.action;
      if (key === 'reassign') { st.mode = 'assign'; return draw(); }
      if (key === 'check') { st.mode = 'check'; return draw(); }
      if (key === 'block') { st.mode = 'block'; st.draft = { since: dayKey() }; return draw(); }
      if (key === 'return') { st.mode = 'return'; st.draft = {}; draw(); root.querySelector('[data-return]')?.focus(); return; }
      return act(key);
    }
    const pick = ev.target.closest('[data-pick]');
    if (pick && !st.busy) {
      const id = pick.dataset.pick;
      const person = id ? st.people.find((p) => p.userId === id) || null : null;
      return act(st.mode === 'check' ? 'check' : 'reassign', { person });
    }
    const chip = ev.target.closest('[data-wait]');
    if (chip) { st.draft.waitingOnType = chip.dataset.wait; st.error = ''; return keepFocus(draw); }
    if (ev.target.closest('[data-block-save]') && !st.busy) {
      return act('block', { draft: { ...st.draft } });
    }
    if (ev.target.closest('[data-return-save]') && !st.busy) {
      const text = String(st.draft.text || '').trim();
      if (!text) { st.error = 'Say what needs changing — it goes in the activity trail.'; return draw(); }
      return act('return', { text });
    }
  });
  root.addEventListener('input', (ev) => {
    const f = ev.target;
    if (f.dataset.bf) st.draft[f.dataset.bf] = f.value;
    if (f.matches('[data-return]')) {
      st.draft.text = f.value;
      const b = root.querySelector('[data-return-save]');
      if (b) b.disabled = !f.value.trim();
    }
  });
  root.addEventListener('change', (ev) => {
    const f = ev.target;
    if (f.dataset.bf) st.draft[f.dataset.bf] = f.value;
  });
  root.addEventListener('keydown', async (ev) => {
    const input = ev.target.closest('[data-comment]');
    if (input && ev.key === 'Enter' && input.value.trim()) {
      ev.preventDefault();
      const text = input.value.trim();
      input.value = '';
      try { await onComment(text); } catch (err) { st.error = `The comment did not save — ${(err && err.message) || err}`; draw(); return; }
      reloadTrail();
    }
  });

  return {
    get taskId() { return st.task.id; },
    close,
    /** The page calls this after a repaint, so the panel shows the task as it now stands. */
    refresh(task, ctx = {}) {
      if (closed || !task) return;
      st.task = task;
      if (ctx.tasks) st.tasks = ctx.tasks;
      if ('project' in ctx) st.project = ctx.project;
      if (st.busy) return;
      keepFocus(draw);
    },
    /** Shown in the panel when a write it asked for is refused. */
    fail(msg) { if (!closed) { st.error = msg; draw(); } },
  };
}
